import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

import { Box, Tooltip } from '@mui/material';
import { SCUIButton } from '../../../../../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../../../../../atomic/molecules';
import { CheckCircle, Cancel, West, East } from '@mui/icons-material';

import { useStorePickupPackageContext } from '../../../../../../context/StorePickupPackageContext';
import { StorePickupPackageCommand } from '../../../../../../../../services/command';
import Helper from '../../../../../../../../utils/helper';
import { SPP } from '../../../../../../storePickupPackage.classes';

import '../../../style.scss';

export const AgreementSummaryTab = ({ setCurrentStep, currentStep }: any) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const {
        packageList,
        damagedRows,
        scannedRows,
        setIsLoading,
        isLoading,
        setPackageList,
        setDamagedRows,
        setScannedRows,
    } = useStorePickupPackageContext();

    const handleBack = () => {
        setCurrentStep(0);
    };

    const handleSubmit = () => {
        if (scannedRows?.length === 0) {
            history.push(`${location.pathname}?tab=1`);
            return;
        }
        Swal.fire({
            text: t('storePickupPackageAdmission.checkAgreementSubmit'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.confirm'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                StorePickupPackageCommand.SubmitStorePickupPackageList({
                    shipmentNumbers: (scannedRows || []).map((item: any) => item.shipmentNumber),
                    storeCode: Helper.getStoreCode(),
                    operatorId: Helper.getOperatorId(),
                    operatorName: Helper.getUserName(),
                })
                    .then((res: any) => {
                        if (res) {
                            if (res.approvedShipments?.length > 0) {
                                setScannedRows([]);
                                setDamagedRows([]);
                                setPackageList(null);
                                setCurrentStep(0);
                                history.push(`${location.pathname}?tab=1`);
                            } else {
                                Swal.fire(t('general.anErrorOccured'), '', 'error');
                            }
                            setIsLoading(false);
                        }
                    })
                    .catch(() => {
                        setIsLoading(false);
                        Swal.fire(t('general.anErrorOccured'), '', 'error');
                    });
            }
        });
    };

    const toBeScannedPackages = useMemo(() => {
        const damagedSet = new Set((damagedRows || []).map((row) => row.packageBarcode));
        const scannedSet = new Set((scannedRows || []).map((row) => row.packageBarcode));

        return (packageList || []).filter(
            (pkg) =>
                !damagedSet.has(pkg.packageBarcode) &&
                !scannedSet.has(pkg.packageBarcode) &&
                !pkg.isMissing
        );
    }, [packageList, damagedRows, scannedRows]);

    const missingPackages = useMemo(() => {
        return (packageList || []).filter((pkg) => pkg.isMissing);
    }, [packageList]);

    const columns: any[] = [
        { field: 'id', headerName: 'id', flex: 1, hide: true },
        {
            field: 'packageBarcode',
            headerName: t('storePickupPackageAdmission.packageBarcode'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellPackageBarcode} data-package-barcode={params.row.packageBarcode}>
                    {params.row.packageBarcode}
                </span>
            ),
        },
        {
            field: 'shipmentNumber',
            headerName: t('storePickupPackageAdmission.shippingNumber'),
            flex: 1,
            renderCell: (params: any) => {
                const shipmentNumber = params.row.shipmentNumber;

                if (!shipmentNumber) {
                    return (
                        <span className={SPP.cellShipmentNumber} data-shipment-number={shipmentNumber}>
                            {shipmentNumber}
                        </span>
                    );
                }

                return (
                    <a
                        target="_blank"
                        rel="noreferrer"
                        className={`${SPP.cellShipmentNumber} ${SPP.cellShipmentNumberLink}`}
                        data-shipment-number={shipmentNumber}
                        href={`/orderdetail?shippingListNo=${shipmentNumber}`}
                    >
                        {shipmentNumber}
                    </a>
                );
            },
        },
        {
            field: 'shippingCompany',
            headerName: t('storePickupPackageAdmission.cargoCompany'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellShippingCompany} data-shipping-company={params.row.shippingCompany}>
                    {params.row.shippingCompany}
                </span>
            ),
        },
        {
            field: 'storeName',
            headerName: t('storePickupPackageAdmission.pickupStoreName'),
            flex: 1,
            renderCell: (params: any) => {
                const storeName = params.row.storeName || '';

                return (
                    <Tooltip title={storeName} placement="top" arrow>
                        <span className={SPP.cellStoreName} data-store-name={storeName}>
                            {storeName}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'storeCode',
            headerName: t('storePickupPackageAdmission.storeCode'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellStoreCode} data-store-code={params.row.storeCode}>
                    {params.row.storeCode}
                </span>
            ),
        },
        {
            field: 'isDPackage',
            headerName: t('storePickupPackageAdmission.isDPackage'),
            flex: 1,
            renderCell: (params: any) => {
                return params.value ? (
                    <Tooltip title="Success">
                        <CheckCircle
                            sx={{ fontSize: '24px' }}
                            color="success"
                            className={`${SPP.cellIsDPackage} ${SPP.cellIsDPackageOk}`}
                            data-is-d-package="true"
                        />
                    </Tooltip>
                ) : (
                    <Tooltip title="Failed">
                        <Cancel
                            sx={{ fontSize: '24px' }}
                            color="error"
                            className={`${SPP.cellIsDPackage} ${SPP.cellIsDPackageFail}`}
                            data-is-d-package="false"
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'scanTime',
            headerName: t('storePickupPackageAdmission.scanTime'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellScanTime} data-scan-time={params.row.scanTime}>
                    {params.row.scanTime}
                </span>
            ),
        },
    ];

    return (
        <Box className={SPP.summary}>
            <Box className={`${SPP.summarySection} ${SPP.summarySectionReceived}`} sx={{ marginBottom: '36px' }}>
                <p className={SPP.summarySectionTitle} style={{ fontSize: '16px', fontWeight: '600' }}>
                    {t('storePickupPackageAdmission.receivedPackageInfo')}
                </p>
                <Box className={SPP.summarySectionGrid}>
                    <SCUICustomDataGrid rows={scannedRows || []} columns={columns} hideFooter allrecord />
                </Box>
                <Box
                    className={SPP.summaryTotalRow}
                    sx={{ width: '100%', marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}
                >
                    <p className={SPP.summaryTotalAmount} style={{ fontSize: '12px', fontWeight: '600' }}>
                        {t('storePickupPackageAdmission.totalPackageAmount')}: {scannedRows?.length || 0}
                    </p>
                </Box>
            </Box>
            <Box className={`${SPP.summarySection} ${SPP.summarySectionMissing}`} sx={{ marginBottom: '36px' }}>
                <p className={SPP.summarySectionTitle} style={{ fontSize: '16px', fontWeight: '600' }}>
                    {t('storePickupPackageAdmission.missingPackageInfo')}
                </p>
                <Box className={SPP.summarySectionGrid}>
                    <SCUICustomDataGrid
                        rows={missingPackages || []}
                        columns={columns}
                        getRowClassName={() => 'missing-row'}
                        hideFooter
                        allrecord
                    />
                </Box>
                <Box
                    className={SPP.summaryTotalRow}
                    sx={{ width: '100%', marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}
                >
                    <p className={SPP.summaryTotalAmount} style={{ fontSize: '12px', fontWeight: '600' }}>
                        {t('storePickupPackageAdmission.totalPackageAmount')}: {missingPackages?.length || 0}
                    </p>
                </Box>
            </Box>
            <Box className={`${SPP.summarySection} ${SPP.summarySectionMismatch}`} sx={{ marginBottom: '36px' }}>
                <p className={SPP.summarySectionTitle} style={{ fontSize: '16px', fontWeight: '600' }}>
                    {t('storePickupPackageAdmission.mismatchPackageInfo')}
                </p>
                <Box className={SPP.summarySectionGrid}>
                    <SCUICustomDataGrid rows={toBeScannedPackages || []} columns={columns} hideFooter allrecord />
                </Box>
                <Box
                    className={SPP.summaryTotalRow}
                    sx={{ width: '100%', marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}
                >
                    <p className={SPP.summaryTotalAmount} style={{ fontSize: '12px', fontWeight: '600' }}>
                        {t('storePickupPackageAdmission.totalPackageAmount')}: {toBeScannedPackages?.length || 0}
                    </p>
                </Box>
            </Box>
            <Box className={`${SPP.summarySection} ${SPP.summarySectionDamaged}`} sx={{ marginBottom: '36px' }}>
                <p className={SPP.summarySectionTitle} style={{ fontSize: '16px', fontWeight: '600' }}>
                    {t('storePickupPackageAdmission.damagedPackageInfo')}
                </p>
                <Box className={SPP.summarySectionGrid}>
                    <SCUICustomDataGrid rows={damagedRows || []} columns={columns} hideFooter allrecord />
                </Box>
                <Box
                    className={SPP.summaryTotalRow}
                    sx={{ width: '100%', marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}
                >
                    <p className={SPP.summaryTotalAmount} style={{ fontSize: '12px', fontWeight: '600' }}>
                        {t('storePickupPackageAdmission.totalPackageAmount')}: {damagedRows?.length || 0}
                    </p>
                </Box>
            </Box>
            {currentStep === 1 && (
                <Box
                    className={SPP.summaryActions}
                    sx={{ display: 'flex', flexDirection: 'row', width: '100%', margin: '24px 4px 4px 4px' }}
                >
                    <SCUIButton
                        text={t('storePickupPackageAdmission.updateList')}
                        style={{ marginRight: 'auto', backgroundColor: '#b0b0b0', borderColor: '#a0a0a0' }}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        showStartIcon
                        startIcon={<West sx={{ fill: '#fff' }} />}
                        onClick={handleBack}
                        className={SPP.btnUpdateList}
                    />
                    <SCUIButton
                        text={t('storePickupPackageAdmission.confirmAgreement')}
                        style={{ marginLeft: 'auto' }}
                        type="primary"
                        sizeType="fixed"
                        disabled={isLoading}
                        buttonType="button"
                        size={'m'}
                        endIcon={<East sx={{ fill: '#fff' }} />}
                        onClick={handleSubmit}
                        className={SPP.btnConfirmAgreement}
                    />
                </Box>
            )}
        </Box>
    );
};
